import React, { useState, useRef } from 'react';
import { Card, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { X, Upload, AlertCircle, CheckCircle } from 'lucide-react';
import * as XLSX from 'xlsx';

type UploadMode = 'schema' | 'fieldInstructions';

interface ExcelUploadFormProps {
  category: string;
  subcategory?: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** When 'fieldInstructions', updates the Field Instructions table (upsert by SAP Field Name). When 'schema', updates the data table. */
  uploadMode?: UploadMode;
}

const ExcelUploadForm: React.FC<ExcelUploadFormProps> = ({
  category,
  subcategory,
  isOpen,
  onClose,
  onSuccess,
  uploadMode = 'schema'
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          selectedFile.type === 'application/vnd.ms-excel' ||
          selectedFile.name.endsWith('.xlsx') ||
          selectedFile.name.endsWith('.xls')) {
        setFile(selectedFile);
        setError(null);
        setSuccess(null);
        parseExcelFile(selectedFile);
      } else {
        setError('Please select a valid Excel file (.xlsx or .xls)');
        setFile(null);
      }
    }
  };

  const parseExcelFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (jsonData.length > 0) {
          const headers = (jsonData[0] as any[]) as string[];
          const dataRows = (jsonData.slice(1) as any[]).filter((row: any[]) => 
            row.some((cell: any) => cell !== null && cell !== '')
          );
          setHeaders(headers);
          setPreviewData(dataRows.slice(0, 5)); // Show first 5 rows as preview
        } else {
          setError('Excel file appears to be empty');
        }
      } catch (err) {
        setError('Error parsing Excel file. Please ensure it\'s a valid Excel file.');
        console.error('Excel parsing error:', err);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    if (!subcategory || subcategory.trim() === '') {
      setError('Please select a subcategory from the dropdown before uploading. Subcategory is required to determine which table to update.');
      return;
    }

    console.log('ExcelUploadForm: Starting upload for category:', category, 'subcategory:', subcategory);
    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      formData.append('subcategory', subcategory);

      console.log('=== FRONTEND UPLOAD DEBUG ===');
      console.log('ExcelUploadForm: File details:', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      });
      console.log('ExcelUploadForm: FormData contents:');
      // Log FormData contents in a TypeScript-compatible way
      console.log('  file:', file);
      console.log('  category:', category);
      console.log('  subcategory:', subcategory);
      console.log('ExcelUploadForm: Sending request to API...');
      console.log('=====================================');
      
      const edgeFnUrl = process.env.REACT_APP_EDGE_FUNCTIONS_URL || process.env.REACT_APP_API_URL || 'http://localhost:3002';
      const url = uploadMode === 'fieldInstructions'
        ? `${edgeFnUrl}/upload/upload-field-instructions`
        : `${edgeFnUrl}/upload/upload-schema`;

      const token = localStorage.getItem('auth_token');
      const orgId = localStorage.getItem('active_org_id');
      const schema = localStorage.getItem('active_schema');
      const hdrs: Record<string, string> = {};
      if (token) hdrs['Authorization'] = `Bearer ${token}`;
      if (orgId) hdrs['X-Organization-Id'] = orgId;
      if (schema) hdrs['X-Schema'] = schema;
      const response = await fetch(url, {
        method: 'POST',
        headers: hdrs,
        body: formData,
      });

      console.log('ExcelUploadForm: Response status:', response.status);
      const result = await response.json();
      console.log('ExcelUploadForm: Response data:', result);

      if (response.ok && result.success) {
        const isFieldInstructions = uploadMode === 'fieldInstructions';
        const successMessage = isFieldInstructions
          ? (result.message || 'Field Instructions updated successfully.')
          : (result.message || 'Excel schema uploaded successfully!');
        const warningMessage = !isFieldInstructions && result.data?.warnings ? ` ${result.data.warnings}` : '';
        const detailMessage = isFieldInstructions
          ? (result.updated !== undefined || result.inserted !== undefined
              ? ` (${result.inserted ?? 0} inserted, ${result.updated ?? 0} updated)`
              : '')
          : (result.data?.rowsFailed > 0
              ? ` (${result.data.rowsSuccessful} successful, ${result.data.rowsFailed} failed)`
              : result.data?.rowsProcessed
                ? ` (${result.data.rowsProcessed} rows processed)`
                : '');

        setSuccess(`${successMessage}${warningMessage}${detailMessage}`);
        console.log('ExcelUploadForm: Upload successful, calling onSuccess...');
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 2000);
      } else {
        const errorMessage = result.message || result.error || 'Upload failed. Please try again.';
        setError(errorMessage);
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Upload error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError(null);
    setSuccess(null);
    setPreviewData([]);
    setHeaders([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]" role="dialog" aria-modal="true" aria-labelledby="excel-upload-title">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 id="excel-upload-title" className="text-xl font-semibold text-gray-900">
              {uploadMode === 'fieldInstructions' ? 'Upload Field Instructions' : 'Upload Excel Schema'}
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
              className="p-2"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-6">
            {/* Subcategory Info */}
            {subcategory ? (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">Subcategory:</span> {subcategory}
                </p>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Please select a subcategory from the dropdown before uploading.
                </p>
              </div>
            )}

            {/* File Upload Button */}
            <div className="text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                tabIndex={-1}
                aria-hidden="true"
              />
              <Button
                variant="outline"
                type="button"
                onClick={() => {
                  if (!subcategory) return;
                  const input = fileInputRef.current;
                  if (input) {
                    input.value = '';
                    setTimeout(() => input.click(), 0);
                  }
                }}
                className="px-6 py-3"
                disabled={!subcategory}
              >
                <Upload className="w-4 h-4 mr-2" />
                Choose File
              </Button>
              {file && (
                <p className="mt-2 text-sm text-gray-600">
                  Selected: {file.name}
                </p>
              )}
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {success && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <p className="text-sm text-green-600">{success}</p>
              </div>
            )}

            {/* Preview Data */}
            {previewData.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-gray-900">Preview</h3>
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b">
                    <p className="text-sm text-gray-600">
                      Headers: {headers.join(', ')}
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          {headers.map((header, index) => (
                            <th key={index} className="px-3 py-2 text-left font-medium text-gray-700">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {previewData.map((row: any[], rowIndex: number) => (
                          <tr key={rowIndex}>
                            {headers.map((_, colIndex: number) => (
                              <td key={colIndex} className="px-3 py-2 text-gray-900">
                                {row[colIndex] || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Upload Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h4 className="font-medium text-blue-900 mb-2">How it works:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                {uploadMode === 'fieldInstructions' ? (
                  <>
                    <li>• Upload an Excel file with headers matching the Field Instructions table</li>
                    <li>• Required column: <strong>SAP Field Name</strong> (or SAP Fields)</li>
                    <li>• Allowed columns: Database Field Name, Description, Data Type, Field Length, Mandatory, Valid Values, Related Table, Remarks</li>
                    <li>• Rows are matched by SAP Field Name: existing rows are updated (only changed fields), new rows are inserted. No rows are deleted.</li>
                  </>
                ) : (
                  <>
                    <li>• Upload an Excel file with headers in the first row</li>
                    <li>• The system will create or update a database table based on the selected subcategory</li>
                    <li>• Headers will map to table column names (case-insensitive)</li>
                    <li>• Data rows will be upserted into the table for this subcategory</li>
                    <li>• The <strong>Field Instructions</strong> tab shows field definitions (schema), not the uploaded data rows</li>
                  </>
                )}
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!file || !subcategory || isUploading}
                className="flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    {uploadMode === 'fieldInstructions' ? 'Update Field Instructions' : 'Upload Schema'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExcelUploadForm;
